/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AnalyticsTab - Analytics Profesional de Envíos Masivos
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file AnalyticsTab.tsx
 * @version 1.0.0
 * @description Tab de analytics con datos reales de Sentinela API
 * @created 2025-01-27
 * 
 * FEATURES:
 * - ✅ KPIs en tiempo real
 * - ✅ Gráfico de tendencias (área)
 * - ✅ Gauge de tasa de éxito
 * - ✅ Heatmap de horarios
 * - ✅ Tabla de historial con datos reales
 * - ✅ Estilo Apple Aqua Mac
 * - ✅ Dark/Light mode compatible
 */

import React from 'react';
import { Card, Row, Col, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCheckCircle,
  faClock,
  faBolt,
  faExclamationTriangle,
  faSync,
  faDownload,
  faPaperPlane,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useSentinelaJobs, type SendJob } from '../../hooks/useSentinelaJobs';

// ═══════════════════════════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════════════════════════

interface AnalyticsTabProps {
  userId: string;
  language?: 'es' | 'en';
}

// ═══════════════════════════════════════════════════════════════════════════
// CHART COMPONENTS (SVG Nativo - Estilo Apple Aqua)
// ═══════════════════════════════════════════════════════════════════════════

interface AreaChartProps {
  data: { label: string; value: number }[];
  width?: number;
  height?: number;
}

const AreaChart: React.FC<AreaChartProps> = ({ data, width = 1000, height = 250 }) => {
  const padding = 40;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  if (data.length === 0) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--bs-secondary)' }}>Sin datos</span>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - (d.value / maxValue) * chartHeight;
    return { x, y, label: d.label, value: d.value };
  });

  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaData = `${pathData} L ${points[points.length - 1].x} ${padding + chartHeight} L ${padding} ${padding + chartHeight} Z`;

  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      {/* Grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
        const y = padding + chartHeight * (1 - ratio);
        return (
          <g key={i}>
            <line
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="var(--bs-border-color)"
              strokeWidth="1"
              strokeDasharray="4"
              opacity="0.3"
            />
            <text
              x={padding - 10}
              y={y + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--bs-secondary)"
            >
              {Math.round(maxValue * ratio)}
            </text>
          </g>
        );
      })}

      {/* Area gradient */}
      <defs>
        <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Area */}
      <path d={areaData} fill="url(#areaGradient)" />

      {/* Line */}
      <path
        d={pathData}
        fill="none"
        stroke="#3b82f6"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Points */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="5" fill="#3b82f6" stroke="white" strokeWidth="2" />
          {i % 5 === 0 && (
            <text
              x={p.x}
              y={height - 10}
              textAnchor="middle"
              fontSize="10"
              fill="var(--bs-secondary)"
            >
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

interface GaugeProps {
  value: number;
  max?: number;
  size?: number;
}

const SuccessGauge: React.FC<GaugeProps> = ({ value, max = 100, size = 200 }) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 20;
  const percentage = Math.min((value / max) * 100, 100);
  const angle = (percentage / 100) * 270 - 135;

  const startAngle = -135;
  const endAngle = angle;

  const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
  const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
  const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
  const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  const color = percentage >= 95 ? '#10b981' : percentage >= 85 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size}>
      {/* Background arc */}
      <path
        d={`M ${x1} ${y1} A ${radius} ${radius} 0 1 1 ${centerX + radius * Math.cos((135 * Math.PI) / 180)} ${centerY + radius * Math.sin((135 * Math.PI) / 180)}`}
        fill="none"
        stroke="var(--bs-border-color)"
        strokeWidth="15"
        strokeLinecap="round"
      />

      {/* Value arc */}
      <path
        d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
        fill="none"
        stroke={color}
        strokeWidth="15"
        strokeLinecap="round"
      />

      {/* Center text */}
      <text
        x={centerX}
        y={centerY - 10}
        textAnchor="middle"
        fontSize="36"
        fontWeight="600"
        fill="var(--bs-body-color)"
      >
        {value.toFixed(1)}%
      </text>
      <text
        x={centerX}
        y={centerY + 20}
        textAnchor="middle"
        fontSize="14"
        fill="var(--bs-secondary)"
      >
        Tasa de Éxito
      </text>
    </svg>
  );
};

interface HeatmapProps {
  data: number[][];
}

const HeatmapChart: React.FC<HeatmapProps> = ({ data }) => {
  const cellSize = 35;
  const padding = 60;
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const hours = ['0h', '4h', '8h', '12h', '16h', '20h'];

  if (data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--bs-secondary)' }}>
        Sin datos de distribución horaria
      </div>
    );
  }

  const maxValue = Math.max(...data.flat(), 1);

  const getColor = (value: number) => {
    const intensity = value / maxValue;
    if (intensity === 0) return '#e5e7eb';
    if (intensity < 0.25) return '#bfdbfe';
    if (intensity < 0.5) return '#60a5fa';
    if (intensity < 0.75) return '#3b82f6';
    return '#1d4ed8';
  };

  return (
    <svg width={days.length * cellSize + padding + 20} height={hours.length * cellSize + padding + 20}>
      {/* Day labels */}
      {days.map((day, i) => (
        <text
          key={`day-${i}`}
          x={padding + i * cellSize + cellSize / 2}
          y={padding - 15}
          textAnchor="middle"
          fontSize="12"
          fontWeight="600"
          fill="var(--bs-body-color)"
        >
          {day}
        </text>
      ))}

      {/* Hour labels */}
      {hours.map((hour, i) => (
        <text
          key={`hour-${i}`}
          x={padding - 15}
          y={padding + i * (cellSize * 4) + cellSize / 2}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize="11"
          fill="var(--bs-secondary)"
        >
          {hour}
        </text>
      ))}

      {/* Cells - agrupadas cada 4 horas */}
      {data.map((dayData, dayIndex) =>
        [0, 4, 8, 12, 16, 20].map((hourStart, groupIndex) => {
          const sum = dayData.slice(hourStart, hourStart + 4).reduce((a, b) => a + b, 0);
          return (
            <g key={`${dayIndex}-${groupIndex}`}>
              <rect
                x={padding + dayIndex * cellSize}
                y={padding + groupIndex * cellSize}
                width={cellSize - 2}
                height={cellSize - 2}
                fill={getColor(sum)}
                rx="4"
              />
              {sum > 0 && (
                <text
                  x={padding + dayIndex * cellSize + cellSize / 2}
                  y={padding + groupIndex * cellSize + cellSize / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill="white"
                >
                  {sum}
                </text>
              )}
            </g>
          );
        })
      )}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ userId, language = 'es' }) => {
  const { jobs, metrics, isLoading, error, refetch } = useSentinelaJobs(userId, 100);

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: KPI CARDS
  // ───────────────────────────────────────────────────────────────────────

  const renderKPIs = () => (
    <Row className="mb-4">
      <Col md={3}>
        <Card style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <Card.Body>
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '8px' }}>
              Total Envíos
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: 'var(--bs-body-color)' }}>
              {metrics?.totalJobs || 0}
            </div>
            <small style={{ color: '#3b82f6' }}>
              <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
              Últimos 30 días
            </small>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <Card.Body>
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '8px' }}>
              Contactos Enviados
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#10b981' }}>
              {metrics?.totalContactsSent.toLocaleString() || 0}
            </div>
            <small style={{ color: 'var(--bs-secondary)' }}>
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              Total exitosos
            </small>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <Card.Body>
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '8px' }}>
              Tasa de Éxito
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#10b981' }}>
              {metrics?.successRate.toFixed(1) || 0}%
            </div>
            <small style={{ color: 'var(--bs-secondary)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              Global
            </small>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)'
        }}>
          <Card.Body>
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '8px' }}>
              Velocidad Promedio
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#f59e0b' }}>
              {metrics?.averageSpeed.toFixed(0) || 0}
            </div>
            <small style={{ color: 'var(--bs-secondary)' }}>
              <FontAwesomeIcon icon={faBolt} className="me-1" />
              mensajes/minuto
            </small>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TREND CHART
  // ───────────────────────────────────────────────────────────────────────

  const renderTrendChart = () => {
    const chartData = metrics?.jobsByDay.slice(-30).map(d => ({
      label: new Date(d.date).toLocaleDateString('es-CL', { month: 'short', day: 'numeric' }),
      value: d.sent
    })) || [];

    return (
      <Card style={{
        border: 'none',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'var(--bs-body-bg)',
        marginBottom: '24px'
      }}>
        <Card.Header style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              <FontAwesomeIcon icon={faChartLine} className="me-2" />
              Tendencia de Envíos (Últimos 30 días)
            </h5>
            <Badge bg="primary">Datos Reales</Badge>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
          <AreaChart data={chartData} width={1050} height={250} />
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: GAUGE + STATS
  // ───────────────────────────────────────────────────────────────────────

  const renderGaugeAndStats = () => (
    <Row className="mb-4">
      <Col lg={5}>
        <Card style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)',
          height: '100%'
        }}>
          <Card.Header style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderBottom: '1px solid var(--bs-border-color)',
            padding: '16px 20px'
          }}>
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              🎯 Tasa de Éxito Global
            </h5>
          </Card.Header>
          <Card.Body style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
            <SuccessGauge value={metrics?.successRate || 0} />
          </Card.Body>
        </Card>
      </Col>

      <Col lg={7}>
        <Card style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--bs-body-bg)',
          height: '100%'
        }}>
          <Card.Header style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderBottom: '1px solid var(--bs-border-color)',
            padding: '16px 20px'
          }}>
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              📊 Estadísticas Detalladas
            </h5>
          </Card.Header>
          <Card.Body style={{ padding: '24px' }}>
            <Row>
              <Col md={6} className="mb-3">
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: 'var(--bs-body-bg)', 
                  border: '1px solid var(--bs-border-color)', 
                  borderRadius: '8px' 
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    Completados
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#10b981' }}>
                    {metrics?.completedJobs || 0}
                  </div>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: 'var(--bs-body-bg)', 
                  border: '1px solid var(--bs-border-color)', 
                  borderRadius: '8px' 
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    En Proceso
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#3b82f6' }}>
                    {metrics?.processingJobs || 0}
                  </div>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: 'var(--bs-body-bg)', 
                  border: '1px solid var(--bs-border-color)', 
                  borderRadius: '8px' 
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    Duración Promedio
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--bs-body-color)' }}>
                    {metrics?.averageDuration.toFixed(1) || 0} min
                  </div>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: 'var(--bs-body-bg)', 
                  border: '1px solid var(--bs-border-color)', 
                  borderRadius: '8px' 
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '4px' }}>
                    Con Errores
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#ef4444' }}>
                    {metrics?.errorJobs || 0}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: HEATMAP
  // ───────────────────────────────────────────────────────────────────────

  const renderHeatmap = () => (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Header style={{
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)',
        padding: '16px 20px'
      }}>
        <h5 className="mb-0" style={{ fontWeight: 600 }}>
          🔥 Distribución Horaria de Envíos
        </h5>
      </Card.Header>
      <Card.Body style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
        <HeatmapChart data={metrics?.jobsByHour || []} />
      </Card.Body>
      <Card.Footer style={{
        backgroundColor: 'var(--bs-body-bg)',
        borderTop: '1px solid var(--bs-border-color)',
        padding: '12px 20px'
      }}>
        <small style={{ color: 'var(--bs-secondary)' }}>
          💡 Intensidad de color indica cantidad de mensajes enviados en esa franja horaria
        </small>
      </Card.Footer>
    </Card>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: HISTORY TABLE
  // ───────────────────────────────────────────────────────────────────────

  const renderHistoryTable = () => {
    const recentJobs = jobs.slice(0, 20);

    return (
      <Card style={{
        border: 'none',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'var(--bs-body-bg)'
      }}>
        <Card.Header style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              📋 Historial de Envíos
            </h5>
            <Button variant="outline-primary" size="sm" style={{ borderRadius: '8px' }}>
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Exportar
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          <Table hover responsive style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: 'var(--bs-body-bg)' }}>
              <tr>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>Fecha</th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>Archivo</th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>Contactos</th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>Enviados</th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>Éxito</th>
                <th style={{ padding: '12px 20px', fontWeight: 600, fontSize: '0.85rem' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.map((job: SendJob) => {
                const successRate = job.totalContacts > 0 
                  ? (job.sentContacts / job.totalContacts) * 100 
                  : 0;

                return (
                  <tr key={job.jobId}>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                      <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: 'var(--bs-secondary)' }} />
                      {new Date(job.createdAt).toLocaleString('es-CL', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                      <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {job.filePath.split('/').pop()}
                      </div>
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                      <strong>{job.totalContacts}</strong>
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                      <strong style={{ color: '#10b981' }}>{job.sentContacts}</strong>
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem' }}>
                      <strong style={{
                        color: successRate >= 95 ? '#10b981' : successRate >= 85 ? '#f59e0b' : '#ef4444'
                      }}>
                        {successRate.toFixed(1)}%
                      </strong>
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      <Badge
                        bg={
                          job.status === 'completed' ? 'success' :
                          job.status === 'processing' ? 'primary' :
                          job.status === 'queued' ? 'warning' :
                          job.status === 'error' ? 'danger' : 'secondary'
                        }
                        style={{ fontSize: '0.75rem' }}
                      >
                        {job.status === 'completed' && 'Completado'}
                        {job.status === 'processing' && 'Procesando'}
                        {job.status === 'queued' && 'En Cola'}
                        {job.status === 'error' && 'Error'}
                        {job.status === 'pending' && 'Pendiente'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {recentJobs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--bs-secondary)' }}>
              No hay historial de envíos disponible
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spinner animation="border" variant="primary" />
        <p style={{ marginTop: '16px', color: 'var(--bs-secondary)' }}>
          Cargando analytics...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert variant="danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
        <Button variant="primary" onClick={refetch}>
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 style={{ fontWeight: 600, marginBottom: '4px' }}>
            📊 Analytics de Envíos Masivos
          </h4>
          <p style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)', marginBottom: 0 }}>
            Datos reales desde Sentinela API
          </p>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={refetch}
          style={{ borderRadius: '8px' }}
        >
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Actualizar
        </Button>
      </div>

      {/* KPIs */}
      {renderKPIs()}

      {/* Trend Chart */}
      {renderTrendChart()}

      {/* Gauge + Stats */}
      {renderGaugeAndStats()}

      {/* Heatmap */}
      {renderHeatmap()}

      {/* History Table */}
      {renderHistoryTable()}
    </div>
  );
};

export default AnalyticsTab;